import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, RefreshCw, Plus, BarChart3 } from 'lucide-react';
import axios from 'axios';
import MetricCard from '../../components/SalesTargets/MetricCard';
import ProgressChart from '../../components/SalesTargets/ProgressChart';
import TargetMetricsTable from '../../components/SalesTargets/TargetMetricsTable';

const SalesTargetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [progress, setProgress] = useState([]);
  const [progressHistory, setProgressHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchTargetDetails();
    fetchProgressHistory();
  }, [id]);

  const fetchTargetDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/salestargets/${id}`);
      setTarget(response.data.target);
      if (response.data.target?.Metrics) {
        setProgress(response.data.target.Metrics);
      }
    } catch (error) {
      console.error('Error fetching target details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressHistory = async () => {
    try {
      const response = await axios.get(`/api/salestargets/${id}/progress-history?days=30`);
      setProgressHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching progress history:', error);
    }
  };

  const handleCalculateProgress = async () => {
    try {
      setCalculating(true);
      const response = await axios.post(`/api/salestargets/${id}/calculate-progress`);
      setProgress(response.data.progress || []);
      await fetchTargetDetails();
      await fetchProgressHistory();
    } catch (error) {
      console.error('Error calculating progress:', error);
      alert('Failed to calculate progress');
    } finally {
      setCalculating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this sales target?')) {
      return;
    }

    try {
      await axios.delete(`/api/salestargets/${id}`);
      navigate('/salestargets');
    } catch (error) {
      console.error('Error deleting target:', error);
      alert('Failed to delete target');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      Draft: 'bg-gray-100 text-gray-800',
      Active: 'bg-green-100 text-green-800',
      Completed: 'bg-blue-100 text-blue-800',
      Cancelled: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  // Prepare chart data from progress history
  const prepareHistoryChartData = () => {
    if (!progressHistory || progressHistory.length === 0) return [];

    const groupedByDate = {};
    progressHistory.forEach(record => {
      const date = new Date(record.CalculatedDate).toLocaleDateString();
      if (!groupedByDate[date]) {
        groupedByDate[date] = { date, actual: 0, target: 0 };
      }
      groupedByDate[date].actual += parseFloat(record.CurrentValue);
      groupedByDate[date].target += parseFloat(record.TargetValue);
    });

    return Object.values(groupedByDate).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
  };

  // Prepare metrics chart data
  const prepareMetricsChartData = () => {
    if (!progress || progress.length === 0) return [];

    return progress.map(metric => ({
      name: metric.MetricName || metric.MetricType,
      actual: parseFloat(metric.CurrentValue || 0),
      target: parseFloat(metric.TargetValue || 0)
    }));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-600">Loading target details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!target) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <p className="text-gray-600">Target not found</p>
          <button
            onClick={() => navigate('/salestargets')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/salestargets')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{target.TargetName}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {target.TargetLevel} • {target.PeriodType} {target.PeriodYear}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleCalculateProgress}
              disabled={calculating}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${calculating ? 'animate-spin' : ''}`} />
              {calculating ? 'Calculating...' : 'Calculate Progress'}
            </button>
            <button
              onClick={() => navigate(`/salestargets/${id}/edit`)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>
        </div>

        {/* Status Badge */}
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(target.Status)}`}>
          {target.Status}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`${
              activeTab === 'metrics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Metrics
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`${
              activeTab === 'progress'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Progress
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Target Name</label>
                <p className="text-sm text-gray-900">{target.TargetName}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(target.Status)}`}>
                  {target.Status}
                </span>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                <p className="text-sm text-gray-900">{target.Description || '-'}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Target Level</label>
                <p className="text-sm text-gray-900">{target.TargetLevel}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Assigned To</label>
                <p className="text-sm text-gray-900">
                  {target.TargetUserName || target.TargetEntityValue || 'Company-wide'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Period</label>
                <p className="text-sm text-gray-900">
                  {target.PeriodType} {target.PeriodYear}
                  {target.PeriodQuarter && ` Q${target.PeriodQuarter}`}
                  {target.PeriodMonth && ` M${target.PeriodMonth}`}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Date Range</label>
                <p className="text-sm text-gray-900">
                  {formatDate(target.StartDate)} - {formatDate(target.EndDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          {progress && progress.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Progress</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {progress.map((metric) => (
                  <MetricCard key={metric.MetricID} metric={metric} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'metrics' && (
        <div>
          <TargetMetricsTable
            metrics={target.Metrics || []}
            showProgress={true}
            editable={false}
          />
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => navigate(`/salestargets/${id}/edit`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Manage Metrics
            </button>
          </div>
        </div>
      )}

      {activeTab === 'progress' && (
        <div className="space-y-6">
          {/* Current Progress Chart */}
          <ProgressChart
            data={prepareMetricsChartData()}
            type="bar"
            title="Current vs Target"
            height={300}
          />

          {/* Historical Progress Chart */}
          {progressHistory.length > 0 && (
            <ProgressChart
              data={prepareHistoryChartData()}
              type="line"
              title="Progress Over Time (Last 30 Days)"
              height={300}
            />
          )}

          {/* Progress Bars */}
          <ProgressChart
            data={prepareMetricsChartData()}
            type="progress"
            title="Achievement by Metric"
          />
        </div>
      )}
    </div>
  );
};

export default SalesTargetDetail;


